import { useState, useCallback, useRef } from 'react';
import { getConversationHistory, createConversation } from '../../../../../services/conversations';

/**
 * Hook that handles conversation list and selected conversation for the current agent.
 * Calls the service getConversationHistory() directly; keeps local state only (no global store).
 * Same pattern as useHomeChatAgent: shared services + local module state.
 */
export const useHomeChatConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);

  /**
   * Fetches conversation history for the given agent and user from the service.
   * Ignores responses for older requests when switching agents quickly.
   * @param {string} agentId - Agent UUID
   * @param {string|null|undefined} userId - User UUID (uses FALLBACK_USER_ID when falsy)
   * @returns {Promise<Array>} Mapped conversations list
   */
  const fetchConversations = useCallback(async (agentId, userId) => {
    if (!agentId) {
      setConversations([]);
      setError(null);
      return [];
    }

    const requestId = ++fetchIdRef.current;
    try {
      setIsLoadingConversations(true);
      setError(null);
      setConversations([]);

      const response = await getConversationHistory({ userId: "", agentId });
      if (requestId !== fetchIdRef.current) return [];

      const list = response.body || response.conversations || [];

      const mapped = list
        .sort((a, b) => {
          const dateA = new Date(a.lastUpdate || a.startedAt || 0).getTime();
          const dateB = new Date(b.lastUpdate || b.startedAt || 0).getTime();
          return dateB - dateA;
        })
        .map((conv) => ({
          id: conv.conversationUuid,
          uuid: conv.conversationUuid,
          conversationUuid: conv.conversationUuid,
          conversation_uuid: conv.conversationUuid,
          title: conv.title || 'Untitled',
          last_modified: conv.lastUpdate || conv.startedAt,
          startedAt: conv.startedAt,
          agent_id: agentId,
          participants: conv.participants || [],
        }));

      if (requestId !== fetchIdRef.current) return [];
      setConversations(mapped);
      return mapped;
    } catch (err) {
      if (requestId === fetchIdRef.current) {
        console.error('Error fetching conversation history:', err);
        setError(err);
        setConversations([]);
      }
      return [];
    } finally {
      if (requestId === fetchIdRef.current) {
        setIsLoadingConversations(false);
      }
    }
  }, []);

  const selectConversation = useCallback((id) => {
    setSelectedConversationId(id);
    setConversationId(id);
  }, []);

  const clearConversationSelection = useCallback(() => {
    setSelectedConversationId(null);
    setConversationId(null);
  }, []);

  const setLastConversationId = useCallback((id) => {
    setConversationId(id);
  }, []);

  const addConversation = useCallback((conversation) => {
    setConversations((prev) => {
      const existing = prev.findIndex(
        (c) => c.id === conversation.id || c.uuid === conversation.uuid
      );
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], ...conversation };
        return next;
      }
      return [conversation, ...prev];
    });
  }, []);

  /**
   * Creates a new conversation for the given agent.
   * Adds the created conversation to the list and optionally selects it.
   * @param {string} agentId - Agent UUID (assistant UUID)
   * @param {Object} [options] - Optional parameters
   * @param {string} [options.title] - Optional title for the conversation
   * @param {boolean} [options.selectAfterCreate=true] - Whether to select the conversation after creating it
   * @returns {Promise<Object>} The created conversation object
   */
  const createNewConversation = useCallback(async (agentId, options = {}) => {
    if (!agentId) {
      const error = new Error('Agent ID is required to create a conversation');
      setError(error);
      throw error;
    }

    const { title, selectAfterCreate = true } = options;

    try {
      setIsCreatingConversation(true);
      setError(null);

      const response = await createConversation({
        assistantUuid: agentId,
        title: title || 'New Conversation',
      });

      // Extract conversation data from response
      // Response structure may vary: { body: {...} } or direct object
      const responseData = response.body || response.data || response;
      const conversationUuid =
        responseData.conversationUuid ||
        responseData.conversation_uuid ||
        responseData.uuid ||
        responseData.id;

      if (!conversationUuid) {
        throw new Error('Conversation UUID not found in response');
      }

      // Map response to match the conversation structure used in this hook
      const newConversation = {
        id: conversationUuid,
        uuid: conversationUuid,
        conversationUuid: conversationUuid,
        conversation_uuid: conversationUuid,
        title: responseData.title || title || 'Untitled',
        last_modified: responseData.lastUpdate || responseData.last_modified || new Date().toISOString(),
        startedAt: responseData.startedAt || responseData.createdAt || new Date().toISOString(),
        agent_id: agentId,
        participants: responseData.participants || [],
      };

      // Add conversation to the list
      addConversation(newConversation);

      // Select the conversation if requested
      if (selectAfterCreate) {
        selectConversation(conversationUuid);
      }

      return newConversation;
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError(err);
      throw err;
    } finally {
      setIsCreatingConversation(false);
    }
  }, [addConversation, selectConversation]);

  return {
    conversations,
    setConversations,
    selectedConversationId,
    setSelectedConversationId,
    conversationId,
    setConversationId,
    setLastConversationId,
    isLoadingConversations,
    setIsLoadingConversations,
    isCreatingConversation,
    error,
    fetchConversations,
    selectConversation,
    clearConversationSelection,
    addConversation,
    createNewConversation,
  };
};
